// WorkAreaDrawer — the editor of "Works in" (ADR-083 §6): the whole project (a
// switch — a project-wide editor's alone, the scope editor's rule for the last
// "All"), or the places by kind — the places grammar (`PlacesFields`: the
// zones-and-clusters tree and the extra sites, a covered site said and dropped
// by the piece) then the depots and, while the vehicles module is on, the pools
// on the family's plain pickers. Written WHOLE: the server
// keeps the places outside a partial editor's reach (counted on the page, never
// named here), like a scope save keeps the grants it may not touch. The set
// rides the team's row stamp (ADR-084 §4 — a set beneath the team): the shell
// seeds from the team as it is at each open, the save carries the stamp, and a
// stale save shows the line with Load the latest (the team re-read).

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { TeamDetail } from '@ems-app/schemas/teams/responses';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { Picker } from '../../components/patterns/Picker.js';
import { PlacesFields } from '../../components/patterns/PlacesFields.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { useSetTeamWorkArea } from '../../hooks/useTeamMutations.js';
import { fetchTeam, teamsKeys } from '../../hooks/useTeams.js';
import { vehiclePoolsSource } from '../../hooks/useVehiclePools.js';
import { depotsSource } from '../../hooks/useWarehouses.js';
import { useViewerGiver } from '../../hooks/useViewerGiver.js';

const schema = z.object({
  whole_project: z.boolean(),
  zone_ids: z.array(z.number().int()),
  cluster_ids: z.array(z.number().int()),
  site_ids: z.array(z.string()),
  warehouse_ids: z.array(z.string()),
  vehicle_pool_ids: z.array(z.string()),
});
type Values = z.infer<typeof schema>;

type WorkAreaTeam = Pick<TeamDetail, 'id' | 'name' | 'work_area' | 'row_version'>;

export interface WorkAreaDrawerProps {
  projectId: string;
  team: WorkAreaTeam;
  open: boolean;
  onClose: () => void;
}

const seedOf = (a: TeamDetail['work_area']): Values => ({
  whole_project: a.whole_project,
  zone_ids: a.zones.map((z) => z.id),
  cluster_ids: a.clusters.map((c) => c.id),
  site_ids: a.sites.map((s) => s.id),
  warehouse_ids: a.warehouses.map((w) => w.id),
  vehicle_pool_ids: a.vehicle_pools.map((p) => p.id),
});

export function WorkAreaDrawer({ projectId, team, open, onClose }: WorkAreaDrawerProps) {
  const { t } = useTranslation('teams');
  const qc = useQueryClient();
  const save = useSetTeamWorkArea(projectId);
  const vehicles = useEntitlements(projectId).has('has_vehicles');
  // The whole project is a project-wide editor's switch alone (a partial editor reads it, never flips it).
  const giver = useViewerGiver(projectId);
  const depots = useMemo(() => depotsSource(projectId), [projectId]);
  const pools = useMemo(() => vehiclePoolsSource(projectId), [projectId]);

  const shell = useFormShell<Values, WorkAreaTeam>({
    schema,
    row: team,
    values: (r) => seedOf((r ?? team).work_area),
    // The fresh team: a quiet read (a team gone meanwhile is said in the form), into the page's entry once it answers.
    reload: () => readLatest(qc, teamsKeys.detail(projectId, team.id), () => fetchTeam(projectId, team.id, true)),
    onSubmit: async (v, { stamp }) => {
      const area = v.whole_project ? { whole_project: true, zone_ids: [], cluster_ids: [], site_ids: [], warehouse_ids: [], vehicle_pool_ids: [] } : { ...v, vehicle_pool_ids: vehicles ? v.vehicle_pool_ids : [] };
      await save.mutateAsync({ teamId: team.id, body: { ...area, row_version: stamp } });
    },
    onSuccess: onClose,
  });
  const { watch, setValue } = shell.form;
  const whole = watch('whole_project');
  const places = { zone_ids: watch('zone_ids'), cluster_ids: watch('cluster_ids'), site_ids: watch('site_ids') };

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={t('detail.editWorkArea')} description={team.name} submitLabel={t('workArea.save')}>
      <Controller control={shell.form.control} name="whole_project" render={({ field }) => <Toggle checked={field.value} onChange={(on) => field.onChange(on)} label={t('workArea.wholeProject')} disabled={!giver.whole && !field.value} />} />
      {!whole && (
        <>
          <PlacesFields
            projectId={projectId}
            value={places}
            onChange={(next) => {
              setValue('zone_ids', next.zone_ids, { shouldDirty: true });
              setValue('cluster_ids', next.cluster_ids, { shouldDirty: true });
              setValue('site_ids', next.site_ids, { shouldDirty: true });
            }}
          />
          <Controller control={shell.form.control} name="warehouse_ids" render={({ field }) => <Picker multiple selectedDisplay="tags" source={depots} label={t('workArea.depots')} placeholder={t('common:places.depotsPlaceholder')} value={field.value} onChange={(next) => field.onChange(next)} />} />
          {vehicles && <Controller control={shell.form.control} name="vehicle_pool_ids" render={({ field }) => <Picker multiple selectedDisplay="tags" source={pools} label={t('workArea.pools')} placeholder={t('common:places.poolsPlaceholder')} value={field.value} onChange={(next) => field.onChange(next)} />} />}
        </>
      )}
    </DrawerForm>
  );
}
